/**
 * @module components/NetworksTable
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ControlLabel, FormGroup, Table } from 'react-bootstrap';
import { useTable, useFilters, useSortBy } from 'react-table';

import { COLOR_OPEN, COLOR_CLOSE } from '../../libs/display';
import { useLanguageState } from '../../contexts/LanguageProvider';
import { useAppState } from '../../contexts/AppStateProvider';

import { Channels } from '../../libs/networks';
import {
  DefaultColumnFilter,
  SelectColumnFilter,
} from '../../libs/react-table-filters';


const ChannelsReactTable = ({columns, data}) => {
  // Column hook
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    [],
  )

  // Table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    defaultColumn,
    initialState: {
      sortBy: [
          {
              id: 'endtime',
              desc: false
          }
      ]
    }
  }, useFilters, useSortBy);

  // Generate the cell property
  const getCellProps = (cell) => {
    if(cell.column.id === 'endtime') {
      console.log(cell.row.original);
      return {
        style: {
          backgroundColor: (cell.row.original.endtime)?COLOR_CLOSE:COLOR_OPEN,
        }
      }
    }
    return { style: {} };
  };

  return (
    <Table {...getTableProps()} bordered hover>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, idx) => {
              const sort = (
                <span>
                  {column.isSorted
                    ? column.isSortedDesc
                      ? ' 🔽'
                      : ' 🔼'
                    : ''}
                </span>
              );
              return (
                <th {
                  ...column.getHeaderProps(
                    (!column.Filter.name.includes('Select'))
                    ?column.getSortByToggleProps()
                    :{}
                  )}
                >
                  {column.canFilter ? (
                    <FormGroup
                      controlId={`id${idx}`}
                    >
                      <ControlLabel>{column.render('Header')}{sort}</ControlLabel>
                      {column.render('Filter')}
                    </FormGroup>
                  ) : <>{column.render('Header')}{sort}</>}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell, j) => (
                <td {...cell.getCellProps([{
                    className: cell.column.className,
                    style: cell.column.style,
                  }, getCellProps(cell)])}>
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </Table>
  );
};


const ChannelsTable = ({channels}) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  // Create column information
  const columns = useMemo(() => [{
    Header: lang.channel,
    accessor: 'code',
  }, {
    Header: lang.startDate,
    accessor: (originalRow) => 
      (originalRow.starttime) ? originalRow.starttime.format(state.datetimeFormat) : '',
  }, {
    Header: lang.endDate,
    id: 'endtime',
    accessor: (originalRow) =>
      (originalRow.endtime) ? originalRow.endtime.format(state.datetimeFormat) : '',
  }, {
    Header: lang.sampleRate,
    accessor: 'sampleRate',
    Filter: SelectColumnFilter,
  }, {
    Header: lang.sensor,
    accessor: 'sensors[0].description',
  }, {
    Header: lang.azimuth,
    accessor: 'azimuth',
    disableFilters: true,
  }, {
    Header: lang.dip,
    accessor: 'dip',
    disableFilters: true,
  }], [
    state.datetimeFormat,
    lang,
  ]);

  const data = useMemo(() => channels.sort((a, b) => b.starttime - a.starttime), [channels]);

  return (
    <ChannelsReactTable
      columns={columns}
      data={data}
    />
  );
};
  
  
ChannelsTable.propTypes = {
  /**
   * Channels object taken from a Station object
   */
  channels: PropTypes.instanceOf(Channels),
};


ChannelsTable.defaultProps = {
  channels: [],
};


export default ChannelsTable;
